// <emma-chat-turn> — one conversation turn (user query + emma response).
//
// Canonical pattern from DS chats.html (assembly file): an active emma
// turn is a single .chat-bubble--emma containing — in order — result-header
// (T09), results-peek > results-list with provider-cards, and refine-zone
// (quick-chips + refine-input). Everything lives INSIDE the bubble, not
// after it. The .chat-bubble--result collapsible from chat-bubble.html is
// a separate pattern (portal chat-history) — not used here.
//
// Lifts DS chat-bubble.css 1:1 for:
//   - .chat-row, .chat-row--user, .chat-row--emma
//   - .chat-avatar--user (24×24 circular gradient)
//   - .chat-bubble--user (gray-60, sans, asymmetric radius)
//   - .chat-bubble--emma (gray-03, padding 20, vertical stack)
//   - .chat-turn-collapsed-* (gray-05 plate for past turns)
//
// And from provider-card.css for .results-peek + .results-peek--fade.
//
// Discriminates entry.sseData → emma bubble body:
//   - result    → result-header stub (T09) + cards + refine (last only)
//   - scanning  → result-header stub (T09 fills)
//   - oob       → stub (T10 fills)
//   - deploy    → stub (T10 fills)
//   - pick      → stub (T10 fills)
//   - consult   → stub (T10 fills)
//
// Collapsed-plate state: previous=true → compact .chat-turn-collapsed
// widget instead of full bubble. Click expands.

import { LitElement, css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TurnEntry } from '../state/index.js';
import type { ResultCard, SuggestedChip } from '../types/search.js';
import type { SourceCitation } from '../types/sse.js';
import {
  OPEN_SOURCE_EVENT,
  buildOpenSourceDetail,
  shouldInterceptSourceClick,
} from './source-link.js';
import { designTokens } from './design-tokens.js';
import { dispatchStopThinking, stopIcon } from './stop-thinking.js';
import { ctaInlineStyles } from './shared-styles.js';
import { renderMarkdown } from './markdown.js';
import { assetUrl } from '../util/asset-url.js';
import { legacyCopyToClipboard } from '../util/clipboard.js';
import { maxCardsForSurface } from '../util/constants.js';
import { docCardChips } from './doc-card-chips.js';
import { applyTooltipClamp } from './tooltip-clamp.js';
import './emma-chat-avatar.js';
import './emma-chat-card.js';
import './emma-chat-refine.js';
import './emma-chat-result-header.js';
import './emma-chat-quick-chips.js';
import './emma-chat-config-badge.js';

// renderInline / renderMarkdown live in ./markdown.ts (pure, unit-tested) — a
// tiny XSS-safe markdown subset (paragraphs, lists, #/##/### headings, **bold**,
// *italic*, `code`, ``` fenced blocks). Imported above.

/** Resolve the .selected highlight index to the BFF-flagged best-price card.
 *  Single source of truth: BFF [handler_cards.go vmConfigsToCards] computes
 *  the cheapest config in the final post-balancing array and flips
 *  `isBestPrice: true` on exactly one card. The widget honours that — no
 *  client-side re-parsing of price strings (that diverged from the BFF
 *  flag when provider-balancing reordered cards alphabetically).
 *  Falls back to 0 when no card carries the flag (defensive — empty array
 *  or older payloads). `priceUnit` retained as parameter for call-site
 *  compatibility but no longer used. */
function findBestPriceIdx(cards: ReadonlyArray<ResultCard>, _priceUnit: 'hour' | 'month'): number {
  for (let i = 0; i < cards.length; i++) {
    if (cards[i]!.isBestPrice) return i;
  }
  return 0;
}

@customElement('emma-chat-turn')
export class EmmaChatTurn extends LitElement {
  /** Full turn payload from state.turnHistory[i]. */
  @property({ attribute: false })
  entry!: TurnEntry;

  /** True if this is the most-recent turn (refine renders here). */
  @property({ type: Boolean, reflect: true })
  last = false;

  /** True if this is a previous (non-last) turn → renders as collapsed plate. */
  @property({ type: Boolean, reflect: true })
  previous = false;

  /** Open a previous turn's collapsed bubble on arrival instead of leaving it
   *  a one-line plate. Set by message-list for the hydrated (dialog-carry) turn
   *  that holds the user's selected config, so the pick is visible + highlighted
   *  without a manual click. One-directional: seeds `expanded=true` once, the
   *  user can still collapse it. */
  @property({ type: Boolean, attribute: 'force-expanded' })
  forceExpanded = false;

  /** 0-indexed turn position in turnHistory. Used in card IDs (`t{i}c{j}`). */
  @property({ type: Number })
  index = 0;

  /** Resolved cards for a pick_result turn — message-list looks up the
   *  pickedIds (`tXcY` references) in turnHistory and passes the actual
   *  ResultCard objects through, so the turn doesn't need store access. */
  @property({ attribute: false })
  pickedCards: ResultCard[] = [];

  /** True while a refine is in flight — passed from message-list (which
   *  reads store.isSubmitting). Drives <emma-chat-refine>'s submitted
   *  indicator (blue-60 button + checkmark icon) on the LAST turn so the
   *  user sees their query in the input + ✓ button while waiting. */
  @property({ type: Boolean })
  submitting = false;

  /** Live thinking-progress message — surfaces in the refine scanning
   *  indicator below the input when submitting=true && last=true. */
  @property({ type: String, attribute: 'thinking-message' })
  thinkingMessage = '';

  /** When true, hide the refine-zone (chips + trigger + input) on the
   *  active turn — soft gate after `maxRefines`. Mirrors monolith
   *  :3559-3577. The bottom CTA in <emma-chat-message-list> becomes the
   *  primary commit affordance once the gate is active. */
  @property({ type: Boolean, attribute: 'soft-gate' })
  softGate = false;

  /** Globally-selected card id (`tXcY`). Set when the user clicks any
   *  card across the whole conversation — drives the .selected highlight
   *  uniformly across active + collapsed turns. null = no manual choice;
   *  per-turn best-price highlight applies. */
  @property({ type: String, attribute: 'selected-card-id' })
  selectedCardId: string | null = null;

  /** BFF URL prefix for asset resolution — threaded down to <emma-chat-card>
   *  for provider logo / country flag / spot icon, and used inline here
   *  for the user-row avatar. Empty in same-origin landing context. */
  @property({ type: String, attribute: 'api-base' })
  apiBase = '';

  /** Embed surface, threaded from <emma-chat>. "portal" makes RAG source
   *  citations dispatch emma-chat:open-source (in-app Docsie sidebar) instead
   *  of opening a new tab; any other value keeps the native new-tab link. */
  @property({ type: String })
  surface = '';

  /** When previous=true, controls expand state of the collapsed bubble. */
  @state() private expanded = false;

  /** Refine input visibility — DS pattern hides input behind a trigger button.
   *  Chips remain always visible; clicking the .refine-trigger reveals input. */
  @state() private refineExpanded = false;

  /** Choreographed reveal phase for a freshly-pushed result turn.
   *  Mirrors monolith showResponse timeline (audit §8.3, index.html
   *  :3198-3551). Phases (cumulative — each subset persists):
   *    'typing'        — 3 bouncing dots in the empty emma bubble
   *                      (300-900ms in monolith — typingDots.is-active)
   *    'scanning'      — result-header shows Braille spinner + initial
   *                      "Scanning N configurations across providers…"
   *    'cycling'       — text morphs to "Comparing pricing across regions…"
   *    'found'         — header transitions to checkmark + summary
   *    'cards'         — results-peek + cards become visible (CSS stagger
   *                      already drives per-card delay)
   *    'refine'        — refine-zone + bottom CTA fade in
   *  Starts in `'typing'` only for the just-arrived (last) turn with a
   *  result; previous turns and side-bubbles skip the timeline. Reduced-
   *  motion users jump straight to 'refine' (final state) — same content,
   *  no theatre. */
  @state() private animationPhase:
    | 'typing'
    | 'scanning'
    | 'cycling'
    | 'found'
    | 'cards'
    | 'refine' = 'typing';

  /** Tracks active timeline timeouts so `disconnectedCallback` can cancel
   *  them when the host element is detached mid-animation (e.g. user
   *  rapidly resubmits before the prior turn finishes its reveal). */
  private animationTimers: number[] = [];

  /** Set after the FIRST timeline run for this entry — guards against
   *  re-running when Lit re-applies properties (e.g. when `last` flips
   *  from false → true after a fast cancel). */
  private animationStarted = false;

  /** Captured at first render — used for the live "updated N min ago"
   *  label. Mirrors monolith submitTimestamp + 30s setInterval (index.html
   *  :4559-4568). null until the first reveal-timeline schedules. */
  private resultTimestamp: number | null = null;
  /** Re-renders the result-header every 30s so updatedLabel stays fresh. */
  private timestampTimer: number | null = null;
  /** Computed in render — formatted as "just now" / "N min ago". */
  @state() private updatedLabel = 'just now';

  /** True while the last → previous collapse animation is running.
   *  During this 320ms window, `render()` returns the FULL turn
   *  template wrapped in a grid-rows shrinking container — the bubble
   *  itself fades + shrinks instead of snapping to the small collapsed
   *  variant. After the timer fires we re-render to the canonical
   *  collapsed bubble template. */
  @state() private collapseInProgress = false;
  private collapseTimer: number | null = null;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.cancelAnimationTimers();
    this.stopTimestampTimer();
    if (this.collapseTimer !== null) {
      window.clearTimeout(this.collapseTimer);
      this.collapseTimer = null;
    }
  }

  /** Detect last → previous transition and arm the collapse animation. */
  override willUpdate(changed: Map<string, unknown>): void {
    super.willUpdate(changed);
    // Seed the collapsed bubble open when the parent asks (dialog-carry: the
    // turn holding the carried config). One-directional so a later user
    // collapse sticks; guarded on the flip so it doesn't fight manual toggles.
    if (changed.has('forceExpanded') && this.forceExpanded && !this.expanded) {
      this.expanded = true;
    }
    if (changed.has('previous') && this.previous === true && changed.get('previous') === false) {
      this.collapseInProgress = true;
      if (this.collapseTimer !== null) window.clearTimeout(this.collapseTimer);
      this.collapseTimer = window.setTimeout(() => {
        this.collapseInProgress = false;
        this.collapseTimer = null;
      }, 320);
    }
  }

  override updated(_changed: Map<string, unknown>): void {
    // Kick off the timeline once `entry` becomes a result-bearing turn
    // AND this turn is `last` (just-arrived). Skip for previous turns
    // (they appear already-resolved) and side-bubbles (no scanning
    // → found transition makes sense for them).
    if (this.animationStarted) return;
    if (!this.last || !this.entry) return;
    const result = this.entry.sseData.result;
    if (!result || !result.results || result.results.length === 0) return;
    this.animationStarted = true;
    this.scheduleAnimationTimeline();
    this.startTimestampTimer();
  }

  private reducedMotion(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /** Live "updated N min ago" — refresh every 30s while the host is in
   *  the DOM. Mirrors monolith :4559-4568. */
  private startTimestampTimer(): void {
    this.resultTimestamp = Date.now();
    this.updatedLabel = 'just now';
    this.stopTimestampTimer();
    this.timestampTimer = window.setInterval(() => {
      if (this.resultTimestamp === null) return;
      const mins = Math.floor((Date.now() - this.resultTimestamp) / 60_000);
      this.updatedLabel = mins < 1 ? 'just now' : `${mins} min ago`;
    }, 30_000);
  }

  private stopTimestampTimer(): void {
    if (this.timestampTimer !== null) {
      window.clearInterval(this.timestampTimer);
      this.timestampTimer = null;
    }
  }

  /** Schedule the 5-step reveal timeline (matches monolith :3198+
   *  showResponse timeouts). Each step is one @state mutation that
   *  triggers a re-render; CSS handles the actual transitions. */
  private scheduleAnimationTimeline(): void {
    if (this.reducedMotion() || this.entry?.hydrated) {
      // Skip theatre — render the final state immediately. reducedMotion is a
      // user preference; `hydrated` turns are a carried transcript replayed
      // verbatim (dialog-carry), so faking a fresh "Scanning N configs…"
      // sequence on an already-answered turn would be jarring on portal landing.
      this.animationPhase = 'refine';
      return;
    }
    const after = (delay: number, fn: () => void): void => {
      this.animationTimers.push(window.setTimeout(fn, delay));
    };
    // t=0 starts in 'typing' (default). Delays match monolith
    // showResponse exactly (index.html :3431-3549) for the
    // typing/scanning/cycling/found beats; the cards & refine beats
    // are pushed back to give the new grid-rows reveal animations
    // room to play sequentially:
    //   900ms:  typing dots removed, respIntro + scanning text appear
    //   1200ms: cycleStep starts (text begins fading out)
    //   1360ms: text becomes "Comparing pricing across regions…"
    //   2520ms: text becomes "Found N best deals", is-found applied
    //   2950ms: results-peek-wrap.is-revealed → wrap grows 0fr→1fr
    //           over 360ms, then 3 cards stagger-fade in 80ms apart
    //           with --duration-arrival each (~240ms). Last card
    //           visible around 2950 + 360 + 80*2 + 240 = 3790ms.
    //   3800ms: refine-zone-wrap.is-revealed → wrap grows then
    //           refine content fades in (360ms + 320ms). Lands the
    //           full UI at ~4480ms, slightly later than monolith
    //           but the choreography is now sequential rather than
    //           overlapping (per UX request: bubble grows → cards
    //           stagger → refine chips).
    after(900, () => (this.animationPhase = 'scanning'));
    after(1360, () => (this.animationPhase = 'cycling'));
    after(2520, () => (this.animationPhase = 'found'));
    after(2950, () => (this.animationPhase = 'cards'));
    after(3800, () => {
      this.animationPhase = 'refine';
      // Once content has fully landed (cards + refine-zone visible),
      // scroll the chat-frame to the top of the viewport. Earlier
      // scrolls (e.g. at t=100ms) drift as content keeps appearing —
      // doing it here means the user lands on a stable layout.
      this.dispatchEvent(
        new CustomEvent('emma-turn-revealed', {
          bubbles: true,
          composed: true,
        }),
      );
    });
  }

  private cancelAnimationTimers(): void {
    for (const id of this.animationTimers) window.clearTimeout(id);
    this.animationTimers = [];
  }

  /** Resolve whether card at index `i` should render as .selected.
   *  Single-selection rule: at most ONE card across the entire chat is
   *  selected at any time.
   *    - Manual selection (selectedCardId) wins by exact `tXcY` match.
   *    - With no manual selection, only the LAST turn's best-price card
   *      lights up as the implicit "current best deal". Past (collapsed)
   *      turns render no selection — otherwise expanding history would
   *      show one selected card per turn, contradicting the rule. */
  private computeSelected(i: number, bestIdx: number): boolean {
    if (this.selectedCardId) {
      return this.selectedCardId === `t${this.index}c${i}`;
    }
    return this.last && i === bestIdx;
  }

  static override styles = [
    designTokens,
    ctaInlineStyles,
    // Lifted 1:1 from design-system/components/chat-bubble.css —
    // chat rows, user/emma bubbles, collapsed plate. Avatar styling
    // lives inside <emma-chat-avatar> (separate component).
    css`
      /* :host as a vertical flex column with gap mirrors DS .chat-frame
         (search-results.html :22-30) — a single gap value separates
         user-row from emma-row inside a turn. <emma-chat-message-list>
         applies the same gap between turns, so the visual rhythm is
         uniform across the whole conversation (DS-flat behavior with
         per-turn component encapsulation). */
      :host {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
      }

      /* ---- Chat row wrappers (DS chat-bubble.css :1-12) ---- */
      .chat-row {
        display: flex;
        gap: 8px;
      }
      .chat-row--user {
        justify-content: flex-end;
        align-items: flex-end;
      }
      .chat-row--emma {
        align-items: flex-start;
      }

      /* ---- System/status event (host-injected deploy line). Reuses the emma
         bubble (.chat-row--emma / .chat-bubble--emma) — only the body text +
         the follow-up/Start-over spacing are local. */
      .system-event-text {
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: 1.5;
        color: var(--gray-80);
      }
      /* Optional host action under the text — link-styled button (an offer the
         user may take, e.g. "Open a support ticket"), NOT a primary CTA. */
      .system-event-action {
        display: inline-block;
        margin-top: 8px;
        padding: 0;
        border: none;
        background: none;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: 1.5;
        color: var(--blue-accent);
        text-decoration: underline;
        text-underline-offset: 2px;
        cursor: pointer;
      }
      .system-event-action:hover,
      .system-event-action:focus-visible {
        color: var(--blue-80);
      }
      .system-followup {
        display: block;
        margin-top: 12px;
      }

      /* ---- Doc card (host-injected help article, <emma-chat>.showDocCard).
         Deliberately NOT a new visual language: it is the SAME emma bubble as
         every other answer, with an eyebrow + title + excerpt + two link-style
         actions reusing the .system-event-action treatment. No card chrome,
         no accent border — the bubble already frames it. */
      .doc-card__eyebrow {
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: var(--color-text-muted);
        opacity: 0.7;
      }
      .doc-card__title {
        margin-top: 2px;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: 600;
        line-height: 1.4;
        color: var(--color-text);
      }
      .doc-card__excerpt {
        margin-top: 6px;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: 1.5;
        color: var(--gray-80);
      }
      .doc-card__actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-3);
        margin-top: 10px;
      }
      .doc-card__action {
        padding: 0;
        border: none;
        background: none;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        line-height: 1.5;
        color: var(--blue-accent);
        text-decoration: underline;
        text-underline-offset: 2px;
        cursor: pointer;
      }
      .doc-card__action:hover,
      .doc-card__action:focus-visible {
        color: var(--blue-80);
      }
      .doc-card__action[data-copied='true'] {
        color: var(--color-text-muted);
        text-decoration: none;
        cursor: default;
      }
      .system-startover-row {
        margin-top: 8px;
      }

      /* ---- User avatar (DS .chat-avatar--user :19-25) ---- */
      .chat-avatar--user {
        flex-shrink: 0;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: block;
      }

      /* ---- User bubble (DS .chat-bubble--user :27-38) ---- */
      .chat-bubble--user {
        background: var(--gray-60);
        border-radius: var(--radius-md);
        border-bottom-right-radius: 2px;
        padding: 10px 14px;
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        color: var(--gray-05);
        line-height: 1.5;
        max-width: 85%;
      }

      /* ---- Emma bubble (DS .chat-bubble--emma :96-109) ----
         Bubble height is NOT animated directly. Instead, the inner
         reveal-wrappers (.results-peek-wrap, .refine-zone-wrap) own
         their own grid-rows transitions (0fr → 1fr) which makes the
         bubble's natural height grow smoothly as their content unfolds.
         No overflow:hidden on the bubble — every card / shadow / focus
         ring renders at its full extent. */
      .chat-bubble--emma {
        flex: 1;
        min-width: 0;
        background: var(--gray-03);
        border-radius: var(--radius-md);
        border-top-left-radius: 2px;
        padding: 20px;
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        font-size: var(--text-sm);
        color: var(--color-text);
        line-height: var(--leading-md);
      }

      /* ---- Typing dots — shown in the empty emma bubble during the
         opening 300-900ms of the reveal timeline. Mirrors monolith
         .emma-hero__typing-dots (index.html :2308-2310 + CSS :534-543).
         3 small blue dots bouncing in sequence. */
      .typing-dots {
        display: flex;
        gap: 4px;
        padding: 4px 0;
      }
      .typing-dots span {
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--blue-60);
        animation: typing-dot-bounce 1.2s var(--easing) infinite;
      }
      .typing-dots span:nth-child(2) {
        animation-delay: 0.15s;
      }
      .typing-dots span:nth-child(3) {
        animation-delay: 0.3s;
      }
      @keyframes typing-dot-bounce {
        0%,
        80%,
        100% {
          transform: scale(0.7);
          opacity: 0.4;
        }
        40% {
          transform: scale(1);
          opacity: 1;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .typing-dots span {
          animation: none;
          opacity: 0.7;
        }
      }

      /* Staggered row entry — user-row first (RAF), emma-row 300ms later.
         Mirrors monolith :3243-3248 + :3430-3436. Applied only on the
         just-arrived (last) turn; previous/collapsed turns skip.
         User-row uses a scale-pop (overshoot) — reads as "spoken word"
         landing in place. Emma-row uses the muted translateY fade so the
         large bubble doesn't pop visually (would be jarring at 600px+). */
      :host([last]) .chat-row--emma {
        animation: row-fade-in var(--duration-normal, 200ms) var(--easing) both;
        animation-delay: 300ms;
      }
      :host([last]) .chat-row--user {
        animation: user-bubble-pop 280ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
        animation-delay: 0ms;
        transform-origin: bottom right;
      }
      @keyframes row-fade-in {
        from {
          opacity: 0;
          transform: translateY(4px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @keyframes user-bubble-pop {
        0% {
          opacity: 0;
          transform: scale(0.9);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        :host([last]) .chat-row--user,
        :host([last]) .chat-row--emma {
          animation: none;
        }
      }

      /* ---- Results-peek wrapper (DS provider-card.css :42-56) ---- */
      .results-peek {
        position: relative;
      }
      /* Fade only enabled when 3+ cards */
      .results-peek--fade::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: -2px;
        right: -2px;
        height: 120px;
        background: linear-gradient(to bottom, transparent, var(--results-peek-bg, var(--gray-03)));
        pointer-events: none;
        border-radius: 0 0 var(--radius-md) var(--radius-md);
      }

      /* ---- Collapsible result bubble for previous turns
         (DS .chat-bubble--result + .chat-bubble--result-open, lifted
         from chat-bubble.css :112-185). search-results.html state 2/3
         uses this — NOT the .chat-turn-collapsed plate (which exists
         in chat-bubble.html standalone but isn't used in any canonical
         assembly). Click on .chat-result-header toggles the body grid
         0fr ↔ 1fr. */
      .chat-bubble--result {
        flex: 1;
        min-width: 0;
        background: var(--gray-03);
        border-radius: var(--radius-md);
        border-top-left-radius: 2px;
        overflow: hidden;
      }
      .chat-result-header {
        position: relative;
        display: flex;
        align-items: center;
        gap: var(--space-2);
        padding: 10px 12px;
        cursor: pointer;
        user-select: none;
        background: none;
        border: none;
        width: 100%;
        font: inherit;
        color: inherit;
        text-align: left;
        /* While the tooltip's :has() carve-out below lifts the bubble's
           overflow clipping, the header's own hover background would bleed
           past the bubble's rounded corners — round it to match. */
        border-radius: inherit;
      }
      .chat-result-header:hover {
        background: var(--gray-05);
      }
      .chat-bubble--result-open .chat-result-header {
        border-bottom: 1px solid var(--color-border);
      }

      /* QA-3 — a collapsed past turn ellipsizes its query; on hover/focus the
         header shows the FULL text in a tooltip (set only when actually
         truncated — see handleCollapsedHeaderHover). Same data-tooltip
         pattern as the quick chips (QA #9), anchored ABOVE the header. */
      .chat-result-header[data-tooltip]::after {
        content: attr(data-tooltip);
        position: absolute;
        bottom: calc(100% + 6px);
        left: 50%;
        transform: translateX(-50%);
        max-width: var(--emma-tt-max, 260px);
        width: max-content;
        padding: 6px 10px;
        background: var(--gray-100);
        color: var(--white);
        font-family: var(--font-sans);
        font-size: 11px;
        font-weight: var(--font-medium);
        line-height: 1.4;
        border-radius: var(--radius-sm);
        white-space: normal;
        text-align: left;
        pointer-events: none;
        opacity: 0;
        z-index: 100;
        transition: opacity var(--duration-fast) var(--easing) 150ms;
      }
      .chat-result-header[data-tooltip][data-tt-align='right']::after {
        left: auto;
        right: 12px;
        transform: none;
      }
      .chat-result-header[data-tooltip][data-tt-align='left']::after {
        left: 12px;
        transform: none;
      }
      .chat-result-header[data-tooltip]:hover::after,
      .chat-result-header[data-tooltip]:focus-visible::after {
        opacity: 1;
      }
      /* The result bubble clips (overflow:hidden, rounded corners) — carve it
         out only WHILE the header tooltip is showing, so the ::after above
         the bubble isn't cut. Browsers without :has() simply keep clipping
         (tooltip hidden, nothing else breaks). */
      .chat-bubble--result:has(.chat-result-header[data-tooltip]:hover),
      .chat-bubble--result:has(.chat-result-header[data-tooltip]:focus-visible) {
        overflow: visible;
      }
      .chat-result-query {
        flex: 1;
        min-width: 0;
        font-size: var(--text-sm);
        color: var(--color-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .chat-result-count {
        flex-shrink: 0;
        font-size: var(--text-xs);
        color: var(--color-text-subtle);
      }
      .chat-result-chevron {
        flex-shrink: 0;
        color: var(--color-text-subtle);
        margin-left: var(--space-1);
        transition: transform var(--duration-normal) var(--easing);
      }
      .chat-bubble--result-open .chat-result-chevron {
        transform: rotate(180deg);
      }
      .chat-result-body {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows var(--duration-collapse) var(--easing);
      }
      .chat-result-body-inner {
        overflow: hidden;
        min-height: 0;
      }
      .chat-result-body-inner > * {
        padding: var(--space-3);
      }
      .chat-bubble--result-open .chat-result-body {
        grid-template-rows: 1fr;
      }

      /* ---- Side-bubble bodies (deploy / oob / pick / consultative-only).
         All sit inside .chat-bubble--emma — the wrapper is the same as
         result-state, only the body content differs. */

      /* Always-visible follow-up input on a grounded consultative answer — lets
         the user ask another doc question OR pivot to a config search (the RAG
         bubble otherwise had no input, only chips). Small gap above it. */
      .consultative-followup {
        display: block;
        margin-top: 10px;
      }

      /* QA #16 — follow-up input on NON-result terminal bubbles (OOB /
         deploy_intent / pick_result). Keeps the conversation open (refine +
         Start over) instead of leaving only a history-wiping Start over. */
      .terminal-followup {
        display: block;
        margin-top: 12px;
      }

      /* Lead text used by deploy / oob / consultative — plain prose. */
      .side-bubble-text {
        font-size: var(--text-sm);
        line-height: var(--leading-md);
        color: var(--color-text);
      }

      /* Rendered-markdown blocks (grounded answers): tight, bubble-friendly
         spacing for paragraphs + bullet lists + inline bold/code. */
      .md-p {
        margin: 0 0 8px;
      }
      .md-p:last-child {
        margin-bottom: 0;
      }
      .md-list {
        margin: 0 0 8px;
        padding-left: 20px;
      }
      .md-list:last-child {
        margin-bottom: 0;
      }
      .md-list li {
        margin: 2px 0;
      }
      /* Compact headings inside a chat bubble — no big display sizes, just a
         slightly larger, heavier line with a touch of top spacing. Levels 1-4
         step down gently (capped at 4 in renderMarkdown). */
      .md-h {
        margin: 10px 0 4px;
        font-weight: 600;
        line-height: 1.3;
        color: var(--color-text);
      }
      .md-h:first-child {
        margin-top: 0;
      }
      .md-h1 {
        font-size: 1.05em;
      }
      .md-h2 {
        font-size: 1em;
      }
      .md-h3,
      .md-h4 {
        font-size: 0.95em;
        color: var(--color-text-muted, var(--color-text));
      }
      /* Fenced code block. The wrapper owns the spacing and anchors the copy
         button, so the button stays put while wide content scrolls sideways
         inside the <pre>. */
      .md-code {
        position: relative;
        margin: 0 0 8px;
      }
      .md-code:last-child {
        margin-bottom: 0;
      }
      .md-pre {
        margin: 0;
        /* Right padding is permanent, not applied on hover: shifting the text
           when the button appears would make every hover jitter. */
        padding: 10px 34px 10px 12px;
        background: var(--color-gray-05, rgba(0, 0, 0, 0.05));
        border-radius: 8px;
        overflow-x: auto;
        max-width: 100%;
        /* Hover-reveal scrollbar — same pattern as the compact chat frame
           (emma-chat.ts). A permanently visible track on a two-line shell
           snippet reads as chrome, not as content. */
        scrollbar-width: thin; /* Firefox */
        scrollbar-color: transparent transparent;
      }
      .md-code:hover .md-pre {
        scrollbar-color: var(--gray-20, #d8dee9) transparent;
      }
      .md-pre::-webkit-scrollbar {
        height: 6px;
      }
      .md-pre::-webkit-scrollbar-track {
        background: transparent;
      }
      .md-pre::-webkit-scrollbar-thumb {
        background: transparent;
        border-radius: 3px;
      }
      .md-code:hover .md-pre::-webkit-scrollbar-thumb {
        background: var(--gray-20, #d8dee9);
      }
      .md-pre code {
        display: block;
        white-space: pre;
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.85em;
        line-height: 1.45;
        background: none;
        padding: 0;
        color: var(--color-text);
      }
      /* Copy affordance — hidden until the block is hovered or the button is
         keyboard-focused, so it never competes with the code itself. */
      .md-copy {
        position: absolute;
        top: 6px;
        right: 6px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        padding: 0;
        border: none;
        border-radius: 6px;
        background: var(--color-gray-10, rgba(0, 0, 0, 0.08));
        color: var(--color-text-muted, var(--color-text));
        cursor: pointer;
        opacity: 0;
        transition:
          opacity 120ms ease,
          background 120ms ease;
      }
      .md-code:hover .md-copy,
      .md-copy:focus-visible {
        opacity: 1;
      }
      .md-copy:hover {
        background: var(--color-gray-20, rgba(0, 0, 0, 0.14));
      }
      .md-copy:focus-visible {
        outline: 2px solid var(--color-primary, currentColor);
        outline-offset: 1px;
      }
      /* Copied: hold the button visible so the confirmation is actually seen
         even if the pointer has already left. */
      .md-copy[data-copied] {
        opacity: 1;
        color: var(--color-success, #2b6b4f);
      }
      .md-copy-done,
      .md-copy[data-copied] .md-copy-idle {
        display: none;
      }
      .md-copy[data-copied] .md-copy-done {
        display: block;
      }
      /* Optimistic pending bubble — typing dots + live thinking label. */
      .pending-thinking {
        display: flex;
        align-items: center;
        gap: 10px;
      }
      .pending-thinking-label {
        font-size: var(--text-sm);
        color: var(--gray-60);
      }
      /* Stop control at the right edge of the pending bubble — cancels the
         in-flight search (bubbles emma-stop-thinking to root <emma-chat>).
         Same visual as the input-overlay / scanning-row stop buttons. */
      .thinking-stop {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
        padding: 4px 10px;
        background: none;
        border: 1px solid var(--gray-10);
        border-radius: var(--radius-sm);
        color: var(--color-text-muted);
        font-family: var(--font-sans);
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        line-height: 1;
        cursor: pointer;
        transition:
          color var(--duration-fast) var(--easing),
          background var(--duration-fast) var(--easing);
      }
      .thinking-stop svg {
        flex: none;
      }
      @media (hover: hover) {
        .thinking-stop:hover {
          color: var(--color-text);
          background: var(--gray-10);
        }
      }
      .side-bubble-text strong,
      .consultative-prefix strong,
      .pick-explanation strong {
        font-weight: 600;
      }
      .side-bubble-text em,
      .consultative-prefix em,
      .pick-explanation em {
        font-style: italic;
      }
      .side-bubble-text code,
      .consultative-prefix code,
      .pick-explanation code {
        font-family: var(--font-mono, ui-monospace, monospace);
        font-size: 0.92em;
        background: var(--color-gray-05, rgba(0, 0, 0, 0.05));
        padding: 1px 4px;
        border-radius: 4px;
        /* Grounded answers put whole shell commands inline; without this a long
           one pushes the bubble past its width instead of wrapping. */
        overflow-wrap: anywhere;
      }

      /* OOB chip suggestions row (DS chat-bubble.css :281-287). */
      .chat-oob-suggestions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-1);
        margin-top: var(--space-1);
      }
      .chat-oob-suggestions .chip--refine {
        display: inline-flex;
        align-items: center;
        gap: var(--space-1);
        padding: 4px 12px;
        border-radius: var(--radius-full);
        border: none;
        background: var(--gray-10);
        font-family: var(--font-sans);
        font-size: var(--text-xs);
        color: var(--color-text-muted);
        cursor: pointer;
        transition:
          background var(--duration-fast) var(--easing),
          color var(--duration-fast) var(--easing);
        white-space: nowrap;
        user-select: none;
      }
      .chat-oob-suggestions .chip--refine:hover {
        background: var(--gray-15);
        color: var(--color-text);
      }
      .chat-oob-suggestions .chip--refine svg {
        flex-shrink: 0;
        opacity: 0.6;
      }
      .chat-oob-suggestions .chip--refine:focus-visible {
        outline: 2px solid var(--blue-60);
        outline-offset: 2px;
      }

      /* Pick result — shows the explanation above and the picked cards
         below in the standard .results-list flex column (same staggered
         entry as result-state). */
      .pick-explanation {
        font-size: var(--text-sm);
        line-height: var(--leading-md);
        color: var(--color-text);
        margin-bottom: var(--space-2);
      }

      /* ---- Hybrid consultative prefix (paragraph between header and
         results when SSE delivers consultative.answer alongside result).
         Plain prose, slight bottom margin to separate from results-peek.
         No DS-specific class — paragraph-scoped styling.
         Smooth arrival — runs on first render in BOTH active turn and
         expanded collapsed bubble so the LLM text fades in instead of
         popping into existence. */
      .consultative-prefix {
        font-size: var(--text-sm);
        color: var(--color-text);
        line-height: var(--leading-md);
        margin-bottom: var(--space-3);
        animation: consultative-arrive 360ms cubic-bezier(0, 0, 0.2, 1) both;
      }
      @keyframes consultative-arrive {
        0% {
          opacity: 0;
          transform: translateY(4px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .consultative-prefix {
          animation: none;
        }
      }

      /* ---- RAG citations under a grounded consultative answer. */
      .consultative-sources {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-1);
        margin-top: var(--space-2);
        font-size: var(--text-xs);
        color: var(--color-text-muted);
      }
      .consultative-sources__label {
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        opacity: 0.7;
      }
      .consultative-sources__item {
        padding: 2px 8px;
        border-radius: var(--radius-full);
        background: var(--gray-10);
        color: var(--color-text-muted);
        text-decoration: none;
        white-space: nowrap;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      a.consultative-sources__item:hover {
        background: var(--gray-15);
        color: var(--color-text);
      }

      /* ---- Fallback banner — amber "filter relaxed" notice.
         Lifted from static/index.html :1421-1445 (.emma-hero__fallback-banner).
         Per design-system/CLAUDE.md this is intentionally prod-only — no
         DS counterpart. Uses DS alert.svg path inline. Icon aligns to the
         FIRST text line (not vertical centre) so multiline copy reads as
         "icon → first-line text" rather than "icon → middle of paragraph". */
      .fallback-banner {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 8px 0 10px;
        padding: 10px 12px;
        background: rgba(250, 173, 20, 0.08);
        border: 1px solid rgba(250, 173, 20, 0.55);
        border-radius: 10px;
        font-family: var(--font-sans);
        font-size: clamp(0.75rem, 0.9vw, 0.8125rem);
        line-height: 1.4;
        color: #8a5c00;
      }
      .fallback-banner svg {
        flex: 0 0 auto;
        /* Pad-down by ~2px so the optical centre of the 16px icon lines
           up with the cap-height of the first line (font ~13px × 1.4
           leading). Matches DS notice rhythm. */
        margin-top: 1px;
      }

      /* ---- Refine notice — informational blue-info banner.
         Lifted from static/index.html :1450-1472 (.emma-hero__refine-notice).
         Cases: no-op refine, provider switch. Distinct from amber fallback.
         Border bumped to --blue-30 (was --blue-20 — too faint per design
         feedback). Uses DS info.svg path inline + first-line alignment. */
      .refine-notice {
        display: flex;
        align-items: flex-start;
        gap: 8px;
        margin: 8px 0 10px;
        padding: 10px 12px;
        background: var(--blue-03);
        border: 1px solid var(--blue-30);
        border-radius: 10px;
        font-family: var(--font-sans);
        font-size: clamp(0.75rem, 0.9vw, 0.8125rem);
        line-height: 1.4;
        color: var(--blue-80);
      }
      .refine-notice svg {
        flex: 0 0 auto;
        margin-top: 1px;
      }

      /* ---- Section reveal wrappers — grid-rows trick.
         Both .results-peek-wrap and .refine-zone-wrap start at
         grid-template-rows: 0fr (zero-height track) and transition to
         1fr (auto height) when the .is-revealed class is applied at
         phase=cards / phase=refine respectively. Inner element has
         overflow:hidden + min-height:0 so content visibly clips during
         the height grow (cards / refine-zone aren't visible yet — they
         fade in via their own delayed animations after the wrap is
         fully open). This makes the BUBBLE's height grow smoothly via
         the inner track expansion — no WAAPI on the outer bubble, no
         visual jitter from offsetHeight measurement timing. */
      .results-peek-wrap,
      .refine-zone-wrap {
        display: grid;
        grid-template-rows: 0fr;
        transition: grid-template-rows 360ms cubic-bezier(0.2, 0, 0, 1);
      }
      .results-peek-wrap.is-revealed,
      .refine-zone-wrap.is-revealed {
        grid-template-rows: 1fr;
      }
      .results-peek-inner {
        overflow: hidden;
        min-height: 0;
      }
      /* Design-review fix 3: the refine zone hosts the chip tooltips
         (data-tooltip ::after pops ABOVE the row) — a permanent
         overflow:hidden here cut them at the zone edge. Clipping during
         the 0fr→1fr grow is unnecessary for THIS zone: .refine-zone
         content is opacity:0 until its delayed arrive animation, so
         nothing can visibly overflow while the track grows; min-height:0
         (not overflow) is what lets the 0fr track collapse. */
      .refine-zone-inner {
        overflow: visible;
        min-height: 0;
      }
      @media (prefers-reduced-motion: reduce) {
        .results-peek-wrap,
        .refine-zone-wrap {
          transition: none;
        }
      }

      /* ---- Refine zone — wraps quick-chips row + refine-input at the
         end of .chat-bubble--emma per DS chats.html assembly. Lift 1:1
         from refine-input.css :107-114: border-top divider separates
         the zone from results above; padding-top + small margin-top
         adjust spacing inside the bubble. The arrive animation only
         fires once the wrap reveals (.is-revealed ancestor), with a
         360ms delay so wrap finishes growing before content fades in. */
      .refine-zone {
        border-top: 1px solid var(--gray-10);
        padding-top: var(--space-3);
        margin-top: var(--space-1);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        opacity: 0;
        transform: translateY(16px);
      }
      .refine-zone-wrap.is-revealed .refine-zone {
        animation: refine-zone-arrive 320ms cubic-bezier(0, 0, 0.2, 1) 360ms forwards;
      }
      @keyframes refine-zone-arrive {
        0% {
          opacity: 0;
          transform: translateY(16px);
        }
        100% {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .refine-zone-wrap.is-revealed .refine-zone {
          animation: none;
          opacity: 1;
          transform: none;
        }
      }

      /* ---- Collapse animation (last → previous transition).
         Grid-rows from 1fr → 0fr is the canonical CSS pattern for
         smoothly animating height-from-auto without measuring in JS.
         Inner block has overflow:hidden so content visibly clips as
         the grid track shrinks. */
      .collapsing-grid {
        display: grid;
        grid-template-rows: 1fr;
        animation: turn-collapse 320ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
      }
      .collapsing-inner {
        overflow: hidden;
        min-height: 0;
      }
      @keyframes turn-collapse {
        to {
          grid-template-rows: 0fr;
          opacity: 0;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .collapsing-grid {
          animation: none;
        }
      }

      /* Card stagger-in: each card sets --card-index inline; CSS drives
         the delay so no JS timeline is needed (replaces monolith setTimeout
         chain — see audit §7.1). Animation starts AFTER the wrap reveal
         (360ms grid-rows transition) — so visually: bubble grows full
         height first (empty inside), then cards fade in one by one with
         80ms stagger.
         Scope: only cards INSIDE .results-peek-wrap start hidden. Cards
         in other contexts (collapsed-bubble expand, pick-result inline
         list) render at default opacity:1 immediately — they don't go
         through the active-turn reveal sequence. */
      .results-peek-wrap .results-list emma-chat-card {
        opacity: 0;
        transform: translateY(8px);
      }
      .results-peek-wrap.is-revealed .results-list emma-chat-card {
        animation: card-arrive var(--duration-arrival) var(--easing-spring) both;
        animation-delay: calc(360ms + var(--card-index, 0) * 80ms);
      }
      @keyframes card-arrive {
        from {
          opacity: 0;
          transform: translateY(8px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .results-peek-wrap .results-list emma-chat-card,
        .results-peek-wrap.is-revealed .results-list emma-chat-card {
          animation: none;
          opacity: 1;
          transform: none;
        }
      }

      /* ---- Result list (lifted from DS provider-card.css .results-list) ---- */
      .results-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }

      @media (prefers-reduced-motion: reduce) {
        .chat-turn-collapsed-chevron,
        .chat-turn-collapsed-body {
          transition: none;
        }
      }
    `,
  ];

  override render() {
    if (!this.entry) return nothing;
    // Display-only system/status event (host-injected via <emma-chat>.pushEvent
    // — e.g. a mirrored deploy-wizard toast). Renders the same whether last or
    // previous (no collapsible result behavior), so deploy context stays
    // legible as the thread grows.
    if (this.entry.systemEvent) return this.renderSystemEvent();
    // Host-injected documentation card (<emma-chat>.showDocCard). Like a
    // system event it never collapses to a one-line plate — the help article
    // the user opened stays readable as the conversation grows past it.
    if (this.entry.docCard) return this.renderDocCard(this.entry.docCard);
    // During the 320ms collapse window we keep rendering the FULL turn
    // inside a grid-rows shrinking container. Both the bubble content
    // AND the surrounding chat-frame slot animate together — no
    // "content snaps + frame catches up" disjoint that the FLIP
    // approach produced.
    if (this.previous && this.collapseInProgress) {
      return html`
        <div class="collapsing-grid">
          <div class="collapsing-inner">${this.renderFullTurn()}</div>
        </div>
      `;
    }
    if (this.previous) {
      return this.renderCollapsedBubble();
    }
    return this.renderFullTurn();
  }

  /** User avatar — DS chats.html turn 1 spec: <img src="...project-1.svg">
   *  24×24 with border-radius 50%. Asset hosted by BFF at /assets/icons/.
   *  Resolved via assetUrl(this.apiBase, ...) so portal embeds load it
   *  from the BFF origin (e.g. https://bff.emma.ms/assets/...) rather
   *  than the host page's origin where the file doesn't exist. */
  private renderUserAvatar() {
    return html`
      <img
        class="chat-avatar--user"
        src=${assetUrl(this.apiBase, '/assets/icons/project/project-1.svg')}
        alt="user"
        width="24"
        height="24"
      />
    `;
  }

  /** QA-3 — show the full query in a tooltip when the collapsed header's
   *  ellipsized .chat-result-query is actually truncated (measured live on
   *  hover/focus; truncation depends on the panel width). Removing the
   *  attribute when it fits keeps the tooltip from firing as noise. */
  private handleCollapsedHeaderHover(e: Event): void {
    const header = e.currentTarget as HTMLElement;
    const queryEl = header.querySelector('.chat-result-query') as HTMLElement | null;
    if (!queryEl) return;
    if (queryEl.scrollWidth > queryEl.clientWidth + 1) {
      header.setAttribute('data-tooltip', this.entry?.query ?? '');
      applyTooltipClamp(header);
    } else {
      header.removeAttribute('data-tooltip');
    }
  }

  /**
   * Previous turn — DS .chat-bubble--result collapsible (search-results.html
   * states 2 & 3). The user query is folded INTO the collapsed bubble's
   * header (.chat-result-query) — there is no separate .chat-row--user
   * for past turns. Only refine queries between turns produce user-rows,
   * and those are owned by the next-turn entry.
   */
  private renderCollapsedBubble() {
    const data = this.entry.sseData;
    const allCards = data.result?.results ?? [];
    const priceUnit = data.result?.priceUnit ?? 'hour';
    const openClass = this.expanded ? ' chat-bubble--result-open' : '';

    // Same cap + best-price logic as the active turn's renderResultBody.
    // Surface-aware slice (portal = all cards, standalone = 3-card teaser)
    // + .results-peek--fade when the standalone cap is hit + bestIdx for
    // .selected highlight (matches monolith expand-collapsed behavior:
    // visible-3-cards rule applies to past turns too via :4159-4166).
    const maxCards = maxCardsForSurface(this.surface);
    const cards = allCards.slice(0, maxCards);
    const bestIdx = findBestPriceIdx(cards, priceUnit);
    const fadeClass = cards.length >= maxCards ? ' results-peek--fade' : '';
    // Header count reflects the FULL pre-cap result count — on standalone
    // the user sees "5 results" with 3 cards + fade hinting "more"; on
    // portal all cards render so the count matches what's visible.
    const headerCount = allCards.length;

    // LLM-side context that would have rendered above the cards in the
    // active turn (consultative prefix when hybrid, fallback banner,
    // refine-notice). Show the same content inside the collapsed body
    // so expanding a previous turn surfaces the full message — not just
    // the cards. Mirrors monolith :4159-4180 expandTurn re-render.
    //
    // Body-text resolution priority — covers every non-result bubble type
    // we render in the active state. First match wins:
    //   1. consultative.answer  — hybrid mode (answer + cards) or
    //      consultative-only ("Talk to engineer")
    //   2. oob.message          — out-of-scope reframe ("I'm here to help…")
    //   3. deploy_intent.message — emma's preamble before the deploy CTA
    //   4. pick_result.explanation — "the second one" → resolved cards
    // Without this fallback chain, expanding a collapsed OOB / deploy /
    // pick turn rendered only the chevron+query header — body was empty.
    const messageText =
      data.consultative?.answer ||
      data.oob?.message ||
      data.deploy_intent?.message ||
      data.pick_result?.explanation ||
      '';
    const fallbackReason = data.fallback?.reason;
    const noticeText = data.result?.notice;

    return html`
      <div class="chat-row chat-row--emma">
        <emma-chat-avatar></emma-chat-avatar>
        <div class="chat-bubble--result${openClass}">
          <button
            class="chat-result-header"
            type="button"
            aria-expanded=${this.expanded ? 'true' : 'false'}
            @click=${this.handleExpand}
            @pointerover=${this.handleCollapsedHeaderHover}
            @focusin=${this.handleCollapsedHeaderHover}
          >
            <span class="chat-result-query">${this.entry.query}</span>
            ${headerCount > 0
              ? html`<span class="chat-result-count"
                  >${headerCount} result${headerCount === 1 ? '' : 's'}</span
                >`
              : nothing}
            <svg class="chat-result-chevron" width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path
                d="M2 4.5l4 4 4-4"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <div class="chat-result-body">
            <div class="chat-result-body-inner">
              <div class="chat-result-body-stack">
                ${messageText
                  ? html`<div class="consultative-prefix">${renderMarkdown(messageText)}</div>`
                  : nothing}
                ${this.renderSources(data.consultative?.sources)}
                ${fallbackReason ? this.renderFallbackBanner(fallbackReason) : nothing}
                ${noticeText ? this.renderRefineNotice(noticeText) : nothing}
                ${cards.length > 0
                  ? html`
                      <div class="results-peek${fadeClass}">
                        <div class="results-list">
                          ${cards.map(
                            (card, i) => html`
                              <emma-chat-card
                                .card=${card}
                                .priceUnit=${priceUnit}
                                ?selected=${this.computeSelected(i, bestIdx)}
                                card-id="t${this.index}c${i}"
                                api-base=${this.apiBase}
                                style="--card-index: ${i}"
                              ></emma-chat-card>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderFullTurn() {
    // Synthetic system-only turns (carry-deploy entry from landing) skip
    // the user-bubble row — there's no question the user actually typed,
    // and rendering an empty <div class="chat-bubble--user"></div> would
    // produce a phantom row. The emma-side bubble still renders normally
    // (either a result card, an oob fallback, or a carry-navigate prompt
    // — see renderEmmaBody / renderCarryNavigatePrompt).
    if (this.entry.isSyntheticSystem) {
      return html`
        <div class="chat-row chat-row--emma">
          <emma-chat-avatar></emma-chat-avatar>
          <div class="chat-bubble--emma">${this.renderEmmaBody()}</div>
        </div>
      `;
    }
    return html`
      <div class="chat-row chat-row--user">
        <div class="chat-bubble--user">${this.entry.query}</div>
        ${this.renderUserAvatar()}
      </div>
      <div class="chat-row chat-row--emma">
        <emma-chat-avatar></emma-chat-avatar>
        <div class="chat-bubble--emma">${this.renderEmmaBody()}</div>
      </div>
    `;
  }

  /** Display-only system/status bubble (host-injected deploy event via
   *  pushEvent). Rendered with the SAME emma bubble as a normal answer (avatar +
   *  .chat-bubble--emma) — the toast TEXT, not toast styling — so it reads as
   *  chat-native. When it is the last turn the bubble also hosts BOTH a
   *  follow-up refine input AND a "Start over" affordance: the event carries no
   *  resolvedParams, so a follow-up refines against the last real result
   *  (unaffected); Start over reuses the OOB clear-conversation path. */
  /** Relay a clicked host-defined action to the <emma-chat> host. The TARGET
   *  event name travels in the DETAIL of a private relay event — never as the
   *  event name itself — so the click can't trigger any of the widget's
   *  in-band handlers on its way up the shadow tree; emma-chat's
   *  handleSystemEventAction re-checks the bound and re-dispatches the real
   *  CustomEvent from the HOST into the light DOM (where the portal listens,
   *  same contract as emma-deploy-click). */
  private dispatchSystemEventAction(eventName: string): void {
    this.dispatchEvent(
      new CustomEvent('emma-system-event-action', {
        detail: { event: eventName },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderSystemEvent() {
    const ev = this.entry!.systemEvent!;
    return html`
      <div class="chat-row chat-row--emma">
        <emma-chat-avatar></emma-chat-avatar>
        <div class="chat-bubble--emma">
          <div class="system-event-text">${ev.text}</div>
          ${ev.action
            ? html`
                <button
                  type="button"
                  class="system-event-action"
                  @click=${() => this.dispatchSystemEventAction(ev.action!.event)}
                >
                  ${ev.action.label}
                </button>
              `
            : nothing}
          ${this.last
            ? html`
                <emma-chat-refine
                  class="system-followup"
                  placeholder="Ask a follow-up, or search for infrastructure…"
                  ?submitted=${this.submitting}
                  thinking-message=${this.thinkingMessage}
                ></emma-chat-refine>
                <div class="chat-oob-suggestions system-startover-row">
                  ${this.renderStartOverChip()}
                </div>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  /** Copy-link feedback: flips the button label to "Copied" for ~1.6s. */
  @state() private docLinkCopied = false;
  private docCopyTimer: number | null = null;

  private handleDocCopy = (url: string): void => {
    const done = (): void => {
      this.docLinkCopied = true;
      if (this.docCopyTimer) window.clearTimeout(this.docCopyTimer);
      this.docCopyTimer = window.setTimeout(() => {
        this.docLinkCopied = false;
        this.docCopyTimer = null;
      }, 1600);
    };
    // navigator.clipboard needs a secure context; the portal is https and
    // localhost counts, but an http:// staging embed would throw — fall back
    // to the execCommand path so the button never silently does nothing.
    const clip = navigator.clipboard;
    if (clip?.writeText) {
      void clip.writeText(url).then(done, () => {
        if (legacyCopyToClipboard(url)) done();
      });
      return;
    }
    if (legacyCopyToClipboard(url)) done();
  };

  /** Documentation card — the portal's Help entry dropped an article into the
   *  transcript (<emma-chat>.showDocCard). Zero LLM calls: everything here is
   *  host/manifest data plus an optional BFF excerpt. Renders inside the
   *  standard emma bubble; the chip row underneath carries origin="help" so a
   *  click routes to the grounding lane (emma-chat.ts handleChipClick), and on
   *  the last turn the bubble also hosts the follow-up refine + Start over,
   *  exactly like the other non-result terminals. */
  private renderDocCard(doc: NonNullable<TurnEntry['docCard']>) {
    const body = doc.excerpt || doc.description || '';
    const chips = docCardChips(doc.chips, doc.title);
    return html`
      <div class="chat-row chat-row--emma">
        <emma-chat-avatar></emma-chat-avatar>
        <div class="chat-bubble--emma">
          <div class="doc-card" part="doc-card">
            <div class="doc-card__eyebrow">Docs</div>
            <div class="doc-card__title">${doc.title}</div>
            ${body ? html`<div class="doc-card__excerpt">${body}</div>` : nothing}
            <div class="doc-card__actions">
              <a class="doc-card__action" href=${doc.url} target="_blank" rel="noopener noreferrer"
                >Open in docs ↗</a
              >
              <button
                type="button"
                class="doc-card__action"
                data-copied=${this.docLinkCopied ? 'true' : 'false'}
                @click=${() => this.handleDocCopy(doc.url)}
              >
                ${this.docLinkCopied ? 'Copied' : 'Copy link'}
              </button>
            </div>
          </div>
          ${chips.length > 0
            ? html` <emma-chat-quick-chips .chips=${chips} origin="help"></emma-chat-quick-chips> `
            : nothing}
          ${this.last
            ? html`
                <emma-chat-refine
                  class="system-followup"
                  placeholder="Ask a follow-up, or search for infrastructure…"
                  ?submitted=${this.submitting}
                  thinking-message=${this.thinkingMessage}
                ></emma-chat-refine>
                <div class="chat-oob-suggestions system-startover-row">
                  ${this.renderStartOverChip()}
                </div>
              `
            : nothing}
        </div>
      </div>
    `;
  }

  /**
   * Discriminate sseData and render bubble body. All states share the
   * .chat-bubble--emma wrapper (DS chats.html canonical pattern); only
   * the body content differs.
   *
   * Result-state (T09): result-header → optional consultative prefix →
   *   optional fallback / notice banners → results-peek > cards →
   *   refine-zone (chips + trigger + input).
   *
   * Side bubbles (T10):
   *   - deploy_intent → text + optional off-ramp chips (CTA lives at
   *                     the bottom — emma-chat-message-list owns it)
   *   - oob          → text + .chat-oob-suggestions chip row
   *   - pick_result  → explanation + .results-list of picked cards
   *   - consultative-only (no result) → answer + optional .chat-cta-inline
   *     ("Talk to engineer" — different funnel from deploy CTA, kept
   *     inline) + optional related_prompts as chips
   *
   * Scanning fallback when none of the above set: result-header
   * scanning-state (Braille spinner).
   */
  private renderEmmaBody() {
    const data = this.entry.sseData;
    // Optimistic pending turn (chip echo): the user bubble is already shown;
    // the emma side is a thinking bubble (typing dots + live progress) until
    // finalizeTurn swaps in the real response.
    if (this.entry.pending) return this.renderPendingThinking();
    const hasResult = !!data.result && !!data.result.results && data.result.results.length > 0;

    // Carry-deploy navigate-mode (K8s landing → portal) takes precedence:
    // turn carries a carryNavigatePrompt with two inline CTAs, no SSE
    // event payloads, no scanning state.
    if (this.entry.carryNavigatePrompt) {
      return this.renderCarryNavigatePrompt(this.entry.carryNavigatePrompt);
    }
    if (hasResult) return this.renderResultBody(data);
    if (data.deploy_intent) return this.renderDeployBubble(data.deploy_intent);
    if (data.pick_result) return this.renderPickBubble(data.pick_result);
    if (data.oob) return this.renderOobBubble(data.oob);
    if (data.consultative && !data.result) {
      return this.renderConsultativeBubble(data.consultative);
    }
    // Scanning state — DS canonical result-header in scanning mode.
    return html` <emma-chat-result-header mode="scanning" .count=${0}></emma-chat-result-header> `;
  }

  /** Stop button in the pending bubble — cancel the in-flight search.
   *  Bubbles emma-stop-thinking to root <emma-chat>, which aborts the
   *  SSE and drops this optimistic pending turn. */
  private handleStopClick = (): void => {
    dispatchStopThinking(this);
  };

  /** Optimistic pending bubble — typing dots + the live thinking message,
   *  matching the synthetic-loader / typing-phase visual language. Shown on a
   *  `pending` turn (chip echo) until the real response replaces it. */
  private renderPendingThinking() {
    return html`
      <div class="pending-thinking">
        <div class="typing-dots" aria-hidden="true"><span></span><span></span><span></span></div>
        ${this.thinkingMessage
          ? html`<span class="pending-thinking-label">${this.thinkingMessage}</span>`
          : nothing}
        <button
          type="button"
          class="thinking-stop"
          aria-label="Stop search"
          @click=${this.handleStopClick}
        >
          ${stopIcon()} Stop
        </button>
      </div>
    `;
  }

  /** Carry-deploy navigate-mode (K8s) — two-CTA prompt bubble.
   *  Used by startWithNavigation in <emma-chat>. The "Continue" button
   *  dispatches a composed emma-navigate-intent event the portal gateway
   *  catches to router.push to managedK8sWizard. The "Refine here"
   *  button is a soft action that just focuses the refine input — the
   *  user can describe what they want and continue the standard search
   *  flow. */
  private renderCarryNavigatePrompt(prompt: NonNullable<TurnEntry['carryNavigatePrompt']>) {
    const handleContinue = (): void => {
      this.dispatchEvent(
        new CustomEvent('emma-navigate-intent', {
          detail: {
            to: 'k8s_wizard',
            workload: prompt.workload,
            utmSource: prompt.utmSource,
          },
          bubbles: true,
          composed: true,
        }),
      );
    };
    const handleRefine = (): void => {
      // emma-carry-refine-here is a soft signal — root component clears
      // carryDeployMode and emma-chat-message-list focuses the refine input.
      this.dispatchEvent(
        new CustomEvent('emma-carry-refine-here', {
          bubbles: true,
          composed: true,
        }),
      );
    };
    return html`
      <div class="side-bubble-text">
        Looks like you want to set up a Kubernetes cluster. Continue to the wizard, or describe what
        you need here first?
      </div>
      <div class="chat-oob-suggestions">
        <button type="button" class="chip--refine" @click=${handleContinue}>
          Continue to wizard
        </button>
        <button type="button" class="chip--refine" @click=${handleRefine}>Refine here</button>
      </div>
    `;
  }

  /** Deploy intent — emma's preamble + optional off-ramp chips.
   *  Per DS rule (search-results.html state 3): only ONE CTA at the
   *  bottom of the conversation under the bubbles, not inline in deploy.
   *  emma-chat-message-list owns the bottom .btn-cta. Chips offer gentle
   *  off-ramps ("make it spot", "smaller GPU") before commit. */
  private renderDeployBubble(deploy: NonNullable<TurnEntry['sseData']['deploy_intent']>) {
    return html`
      <div class="side-bubble-text">${renderMarkdown(deploy.message)}</div>
      ${this.renderOptionalChips(deploy.chips)} ${this.renderTerminalFollowup()}
    `;
  }

  /** OOB — out-of-scope query. Emma's polite redirect + suggestion chips
   *  derived from `oob.suggestions` (string[] — each is both label and
   *  prompt). DS pattern: .chat-oob-suggestions row of .chip--refine.
   *  Always appends a "Start over" chip — for OOB the conversation has
   *  effectively dead-ended (the user asked something off-scope), so
   *  giving them an explicit reset affordance inline is more discoverable
   *  than relying on the global × Clear button. Click dispatches
   *  emma-clear-conversation which the root catches to reset the store. */
  private renderOobBubble(oob: NonNullable<TurnEntry['sseData']['oob']>) {
    const suggestions = oob.suggestions ?? [];
    return html`
      <div class="side-bubble-text">${renderMarkdown(oob.message)}</div>
      <div class="chat-oob-suggestions">
        ${suggestions.map(
          (s) => html`
            <button type="button" class="chip--refine" @click=${() => this.handleOobSuggestion(s)}>
              <svg aria-hidden="true" width="10" height="10" viewBox="0 0 20 20" fill="none">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M10.0129 0.5C10.5652 0.5 11.0129 0.947715 11.0129 1.5V9.01068H18.5C19.0523 9.01068 19.5 9.4584 19.5 10.0107C19.5 10.563 19.0523 11.0107 18.5 11.0107H11.0129V18.5C11.0129 19.0523 10.5652 19.5 10.0129 19.5C9.46066 19.5 9.01294 19.0523 9.01294 18.5V11.0107H1.5C0.947715 11.0107 0.5 10.563 0.5 10.0107C0.5 9.4584 0.947715 9.01068 1.5 9.01068H9.01294V1.5C9.01294 0.947715 9.46066 0.5 10.0129 0.5Z"
                  fill="currentColor"
                />
              </svg>
              ${s}
            </button>
          `,
        )}
        ${this.renderStartOverChip()}
      </div>
      ${this.last ? this.renderFollowupRefine() : nothing}
    `;
  }

  /** Dispatched by the OOB "Start over" chip — bubbles to root which
   *  resets the store and re-mounts <emma-chat-input>. */
  private handleStartOver = (): void => {
    this.dispatchEvent(
      new CustomEvent('emma-clear-conversation', {
        bubbles: true,
        composed: true,
      }),
    );
  };

  /** Shared "Start over" chip — dispatches emma-clear-conversation (via
   *  handleStartOver) to reset the whole conversation. Rendered inline on
   *  terminal bubbles (OOB / deploy_intent / pick_result / system-event) so
   *  the reset affordance is always discoverable there. */
  private renderStartOverChip() {
    return html`
      <button
        type="button"
        class="chip--refine chip--start-over"
        @click=${this.handleStartOver}
        aria-label="Start a new conversation"
      >
        <svg aria-hidden="true" width="11" height="11" viewBox="0 0 16 16" fill="none">
          <path
            d="M3 4v3.5h3.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M3.2 7.5C4 5 6 3.5 8.5 3.5c3 0 5.5 2.5 5.5 5.5s-2.5 5.5-5.5 5.5c-2.5 0-4.5-1.5-5.2-3.5"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        Start over
      </button>
    `;
  }

  /** Follow-up refine input for NON-result terminal bubbles. A refine typed
   *  here runs as a FRESH search — <emma-chat>.lastTurnWasSearch() is false
   *  for these no-result turns, so no stale previousParams / lastResolvedParams
   *  from an earlier result carry over (QA #16 safe default). */
  private renderFollowupRefine() {
    return html`
      <emma-chat-refine
        class="terminal-followup"
        placeholder="Ask a follow-up, or search for infrastructure…"
        ?submitted=${this.submitting}
        thinking-message=${this.thinkingMessage}
      ></emma-chat-refine>
    `;
  }

  /** Last-turn follow-up affordance for terminal bubbles that carry NO
   *  Start over of their own (deploy_intent / pick_result): a refine input
   *  plus a Start over chip, so both affordances coexist (QA #16). OOB keeps
   *  its existing inline Start over and appends only the refine input. */
  private renderTerminalFollowup() {
    if (!this.last) return nothing;
    return html`
      ${this.renderFollowupRefine()}
      <div class="chat-oob-suggestions system-startover-row">${this.renderStartOverChip()}</div>
    `;
  }

  /** Pick result — LLM resolved a reference like "the second one" to specific
   *  cards from prior turns. Renders explanation + the picked cards inline
   *  (parent message-list resolves pickedIds to ResultCards via pickedCards
   *  prop). Optional chips offer next-step suggestions. */
  private renderPickBubble(pick: NonNullable<TurnEntry['sseData']['pick_result']>) {
    const cards = this.pickedCards;
    return html`
      <div class="pick-explanation">${renderMarkdown(pick.explanation)}</div>
      ${cards.length > 0
        ? html`
            <div class="results-list">
              ${cards.map(
                (card, i) => html`
                  <emma-chat-card
                    .card=${card}
                    price-unit="hour"
                    ?selected=${i === 0}
                    card-id="t${this.index}c${i}"
                    api-base=${this.apiBase}
                    style="--card-index: ${i}"
                  ></emma-chat-card>
                `,
              )}
            </div>
          `
        : nothing}
      ${this.renderOptionalChips(pick.chips)} ${this.renderTerminalFollowup()}
    `;
  }

  /** Consultative-only — emma answered without a search (complex query
   *  warranting a human handoff). Render answer + optional CTA (e.g.
   *  "Talk to an engineer →") + optional related_prompts as chips. */
  private renderConsultativeBubble(cons: NonNullable<TurnEntry['sseData']['consultative']>) {
    // Follow-up chips: prefer the structured `chips` (grounded turns put
    // neighbour-article topics here, label≠prompt), else the legacy
    // `related_prompts` (label=prompt). Never both — avoids a doubled chip row.
    const chipItems =
      (cons.chips ?? []).length > 0
        ? (cons.chips ?? []).map((c) => ({ label: c.label, prompt: c.prompt }))
        : (cons.related_prompts ?? []).map((p) => ({ label: p, prompt: p }));
    return html`
      <div class="side-bubble-text">${renderMarkdown(cons.answer)}</div>
      ${this.renderSources(cons.sources)}
      ${cons.cta && cons.cta.url && cons.cta.text
        ? html`
            <a class="chat-cta-inline" href=${cons.cta.url} target="_blank" rel="noopener">
              ${cons.cta.text} →
            </a>
          `
        : nothing}
      ${chipItems.length > 0
        ? html` <emma-chat-quick-chips .chips=${chipItems} origin="help"></emma-chat-quick-chips> `
        : nothing}
      ${this.last
        ? html`
            <emma-chat-refine
              class="consultative-followup"
              placeholder="Ask a follow-up, or search for infrastructure…"
              ?submitted=${this.submitting}
              thinking-message=${this.thinkingMessage}
            ></emma-chat-refine>
            <div class="chat-oob-suggestions system-startover-row">
              ${this.renderStartOverChip()}
            </div>
          `
        : nothing}
    `;
  }

  /** Render RAG citations under a grounded consultative answer. Absent/empty →
   *  nothing, so non-grounded answers look identical to before.
   *
   *  Citations keep their native <a target="_blank"> so hero/standalone embeds
   *  (and a portal with JS disabled) open the doc in a new tab. On surface==
   *  "portal" the click handler intercepts (preventDefault) and emits
   *  emma-chat:open-source for the in-app Docsie sidebar instead — see
   *  handleSourceClick / source-link.ts. */
  private renderSources(sources?: SourceCitation[]) {
    if (!sources || sources.length === 0) return nothing;
    return html`
      <div class="consultative-sources">
        <span class="consultative-sources__label">Sources</span>
        ${sources.map((s) =>
          s.url
            ? html`<a
                class="consultative-sources__item"
                href=${s.url}
                target="_blank"
                rel="noopener"
                @click=${(e: MouseEvent) => this.handleSourceClick(e, s)}
                >${s.title}</a
              >`
            : html`<span class="consultative-sources__item">${s.title}</span>`,
        )}
      </div>
    `;
  }

  /** Portal-only: intercept a source-citation click and dispatch the composed
   *  emma-chat:open-source event (bubbles through the shadow DOM to the
   *  <emma-chat> host the portal listens on). Any other surface returns early,
   *  letting the native <a target="_blank"> open the doc in a new tab. */
  private handleSourceClick(e: MouseEvent, s: SourceCitation): void {
    if (!shouldInterceptSourceClick(this.surface)) return;
    e.preventDefault();
    this.dispatchEvent(
      new CustomEvent(OPEN_SOURCE_EVENT, {
        detail: buildOpenSourceDetail(s),
        bubbles: true,
        composed: true,
      }),
    );
  }

  private renderOptionalChips(chips: SuggestedChip[] | undefined | null) {
    if (!chips || chips.length === 0) return nothing;
    return html` <emma-chat-quick-chips .chips=${chips}></emma-chat-quick-chips> `;
  }

  private handleOobSuggestion(text: string): void {
    // Reuse the same emma-chip-click contract as quick-chips; root treats
    // it identically (re-submits as a fresh user query).
    this.dispatchEvent(
      new CustomEvent('emma-chip-click', {
        detail: { label: text, prompt: text },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Result body — children of .chat-bubble--emma per DS chats.html
   * turn-1 assembly. Canonical order:
   *   1. result-header (found state)
   *   2. consultative prefix (hybrid mode — when LLM answered + searched)
   *   3. fallback banner (cascade triggered)
   *   4. refine notice (no-op refine / provider switch)
   *   5. results-peek > results-list > cards (with --card-index stagger)
   *   6. refine-zone > quick-chips + refine-input (last turn only)
   */
  private renderResultBody(data: typeof this.entry.sseData) {
    const result = data.result!;
    const allCards = result.results ?? [];
    const priceUnit = result.priceUnit ?? 'hour';

    // Surface-aware visible-card cap (maxCardsForSurface): standalone keeps
    // the 3-card teaser per monolith index.html :2477 + :3401 with the fade
    // overlay (.results-peek--fade) as a "designer-rule" visual anchor;
    // portal renders ALL cards with no fade (QA #19 — the "Found N" summary
    // must match what's on screen). Best-price index is computed on the
    // VISIBLE slice so the .selected highlight stays within the cap
    // (monolith :3402 + :2820-2828 findBestPriceIdx).
    const maxCards = maxCardsForSurface(this.surface);
    const cards = allCards.slice(0, maxCards);
    const bestIdx = findBestPriceIdx(cards, priceUnit);
    const fadeClass = cards.length >= maxCards ? ' results-peek--fade' : '';

    // Scanned / providers-queried come from the thinking_complete SSE
    // event (separate from `result`). Monolith :3349-3350 + :3474-3476.
    // BFF tells us how many configs it scanned and how many providers
    // it queried — these are real numbers, not derived from the visible
    // slice. Falls back to 0 when missing so meta-line gracefully omits.
    const totalScanned = data.thinking_complete?.totalScanned ?? 0;
    const providersQueried = data.thinking_complete?.providersQueried ?? 0;

    const consultativePrefix = data.consultative?.answer;

    // Phase gating — mirrors monolith showResponse :3198+ choreography.
    // Each phase reveals more of the bubble content; previous phases'
    // content stays visible (cumulative). Side-bubbles + previous turns
    // skip the timeline (animationPhase stays 'scanning' but they take
    // the non-result branches above).
    //
    // Cards + refine-zone are rendered as soon as we hit `isFound`, but
    // hidden inside grid-rows reveal wrappers (0fr → 1fr). The wrappers
    // get `is-revealed` only when the corresponding phase fires — at
    // which point CSS animates the wrap height (360ms), and the
    // contained cards / refine content fade in AFTER the wrap settles
    // (animation-delay: 360ms). This sequences "bubble expands → cards
    // stagger → refine arrives" without WAAPI height morph (which had
    // measurement-timing jitter and overflow clipping artefacts).
    const phase = this.animationPhase;
    const isTyping = phase === 'typing';
    const isFound = phase === 'found' || phase === 'cards' || phase === 'refine';
    const cardsRevealed = phase === 'cards' || phase === 'refine';
    // Refine-zone hidden once soft-gate fires (post-MAX_REFINES). Bottom
    // CTA in <emma-chat-message-list> takes over as the primary action.
    const refineRevealed = phase === 'refine' && this.last && !this.softGate;
    // Render the refine zone in DOM as soon as we know we'll need it
    // (last turn, not soft-gated, has a result), so the grid-rows
    // animation has a node to reveal. Without this pre-render, switching
    // `nothing` → element on phase change would skip the transition.
    const refineRendered = isFound && this.last && !this.softGate;

    return html`
      ${isTyping
        ? html`
            <div class="typing-dots" aria-hidden="true">
              <span></span><span></span><span></span>
            </div>
          `
        : isFound
          ? html`
              <emma-chat-result-header
                mode="found"
                .count=${cards.length}
                .scanned=${totalScanned}
                .providerCount=${providersQueried}
                .summary=${result.summary || ''}
                .updatedLabel=${this.updatedLabel}
              ></emma-chat-result-header>
            `
          : html`
              <emma-chat-result-header
                mode="scanning"
                .count=${totalScanned}
                scanning-text=${phase === 'cycling' ? 'Comparing pricing across regions…' : ''}
              ></emma-chat-result-header>
            `}
      ${isFound && consultativePrefix
        ? html`<div class="consultative-prefix">${renderMarkdown(consultativePrefix)}</div>`
        : nothing}
      ${this.renderSources(data.consultative?.sources)}
      ${isFound && data.fallback ? this.renderFallbackBanner(data.fallback.reason) : nothing}
      ${isFound && result.notice ? this.renderRefineNotice(result.notice) : nothing}
      ${isFound
        ? html`
            <div class="results-peek-wrap${cardsRevealed ? ' is-revealed' : ''}">
              <div class="results-peek-inner">
                <div class="results-peek${fadeClass}">
                  <div class="results-list">
                    ${cards.map(
                      (card, i) => html`
                        <emma-chat-card
                          .card=${card}
                          .priceUnit=${priceUnit}
                          ?selected=${this.computeSelected(i, bestIdx)}
                          card-id="t${this.index}c${i}"
                          api-base=${this.apiBase}
                          style="--card-index: ${i}"
                        ></emma-chat-card>
                      `,
                    )}
                  </div>
                </div>
              </div>
            </div>
          `
        : nothing}
      ${refineRendered
        ? html`
            <div class="refine-zone-wrap${refineRevealed ? ' is-revealed' : ''}">
              <div class="refine-zone-inner">
                <div class="refine-zone" @emma-chip-click=${this.handleChipClick}>
                  <emma-chat-config-badge
                    .params=${result.resolvedParams ?? null}
                  ></emma-chat-config-badge>
                  <emma-chat-quick-chips
                    .chips=${result.chips ?? []}
                    .results=${cards}
                    .resolvedParams=${result.resolvedParams ?? null}
                    ?show-trigger=${true}
                    ?trigger-open=${this.refineExpanded}
                    @emma-refine-trigger-click=${this.handleRefineTrigger}
                  ></emma-chat-quick-chips>
                  <emma-chat-refine
                    ?hidden=${!this.refineExpanded}
                    ?submitted=${this.submitting}
                    thinking-message=${this.thinkingMessage}
                  ></emma-chat-refine>
                </div>
              </div>
            </div>
          `
        : nothing}
    `;
  }

  /** Amber alert banner — uses DS alert.svg path (design-system/assets/icons/base/alert.svg)
   *  inlined on a 20×20 viewBox so currentColor flows from the banner. */
  private renderFallbackBanner(reason: string) {
    return html`
      <div class="fallback-banner" role="status">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M7.38795 2.3408C8.55335 0.399908 11.3665 0.399907 12.5319 2.3408L19.3558 13.7056C20.5565 15.7052 19.1162 18.2499 16.7839 18.2499H3.13598C0.80366 18.2499 -0.636612 15.7052 0.56401 13.7056L7.38795 2.3408ZM10.8172 3.37036C10.4288 2.72339 9.49106 2.72339 9.1026 3.37036L2.27866 14.7352C1.87845 15.4017 2.35854 16.2499 3.13598 16.2499H16.7839C17.5613 16.2499 18.0414 15.4017 17.6412 14.7352L10.8172 3.37036Z"
            fill="currentColor"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.95991 5.89997C10.5122 5.89997 10.9599 6.34768 10.9599 6.89997V11.04C10.9599 11.5923 10.5122 12.04 9.95991 12.04C9.40763 12.04 8.95991 11.5923 8.95991 11.04V6.89997C8.95991 6.34768 9.40763 5.89997 9.95991 5.89997Z"
            fill="currentColor"
          />
          <path
            d="M10.9999 13.9999C10.9999 14.5522 10.5522 14.9999 9.99992 14.9999C9.44764 14.9999 8.99992 14.5522 8.99992 13.9999C8.99992 13.4477 9.44764 12.9999 9.99992 12.9999C10.5522 12.9999 10.9999 13.4477 10.9999 13.9999Z"
            fill="currentColor"
          />
        </svg>
        <span>${reason}</span>
      </div>
    `;
  }

  /** Blue informational notice — uses DS info.svg path (design-system/assets/icons/base/info.svg)
   *  inlined on a 20×20 viewBox so currentColor flows from the notice. */
  private renderRefineNotice(text: string) {
    return html`
      <div class="refine-notice" role="status">
        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.25 7C9.25 6.586 9.586 6.25 10 6.25C10.414 6.25 10.75 6.586 10.75 7C10.75 7.414 10.414 7.75 10 7.75C9.586 7.75 9.25 7.414 9.25 7ZM9.24998 9.24996C9.24998 8.83596 9.58598 8.49996 9.99998 8.49996C10.414 8.49996 10.75 8.83596 10.75 9.24996V13C10.75 13.414 10.414 13.75 9.99998 13.75C9.58598 13.75 9.24998 13.414 9.24998 13V9.24996ZM9.99999 16C6.69174 16 3.99999 13.3083 3.99999 10C3.99999 6.69179 6.69174 4.00004 9.99999 4.00004C13.3082 4.00004 16 6.69179 16 10C16 13.3083 13.3082 16 9.99999 16ZM10 2.5C5.85775 2.5 2.5 5.85775 2.5 10C2.5 14.1423 5.85775 17.5 10 17.5C14.1415 17.5 17.5 14.1423 17.5 10C17.5 5.85775 14.1415 2.5 10 2.5Z"
            fill="currentColor"
          />
        </svg>
        <span>${text}</span>
      </div>
    `;
  }

  private handleExpand = (): void => {
    this.expanded = !this.expanded;
  };

  private handleRefineTrigger = async (): Promise<void> => {
    this.refineExpanded = !this.refineExpanded;
    if (this.refineExpanded) {
      // Focus input on next paint so users can type immediately.
      await this.updateComplete;
      const refine = this.renderRoot.querySelector('emma-chat-refine');
      refine?.focusInput?.();
    }
  };

  /** Quick-chip click on the LAST turn — mirror monolith :3746-3760:
   *    1. fill refine-input with chip prompt
   *    2. open the refine trigger (expand wrap)
   *    3. submitted-state on refine-wrap (blue-60 button + checkmark)
   *    4. dispatch emma-refine-submit upward so root runs the search
   *  We stop emma-chip-click here so root doesn't ALSO submit it as a
   *  separate refine — refine-submit is the canonical event. */
  private handleChipClick = async (
    e: CustomEvent<{ label: string; prompt: string }>,
  ): Promise<void> => {
    e.stopPropagation();
    const prompt = e.detail.prompt;
    if (!prompt) return;

    // Open the refine zone so the user sees the chip text in the input.
    this.refineExpanded = true;
    await this.updateComplete;

    const refine = this.renderRoot.querySelector('emma-chat-refine');
    refine?.setText?.(prompt);

    // Re-emit as refine-submit so root T12 picks it up via the standard
    // refine pathway (runSearch with isRefine: true). The submitted
    // visual state on refine is driven by ?submitted=${this.submitting}
    // — root flips submitting=true synchronously, so refine wrap shows
    // checkmark immediately when this event is handled.
    this.dispatchEvent(
      new CustomEvent('emma-refine-submit', {
        detail: { text: prompt },
        bubbles: true,
        composed: true,
      }),
    );
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'emma-chat-turn': EmmaChatTurn;
  }
}
